import DecisionScoresSection from "@Components/game/session-results/decision/decision-scores-section";
import BetaLearnMoreDialog from "@Components/game/session-results/feedback/beta-learn-more-dialog";
import FeedbackHeader, { type UserRating } from "@Components/game/session-results/feedback/feedback-header";
import ScenarioRetry from "@Components/game/session-results/feedback/scenario-retry";
import VideoStream from "@Components/game/session-results/video/video-stream";
import { useSessionResultsStaticContext, useSessionResultsTabContext } from "@Context/game/activity/session-results-context";
import { AnalyticsEvents, GameModes } from "@Enums/index";
import type { DecisionInsightsRating } from "@Helpers/decision-insights-rating-storage";
import { loadDecisionInsightsRating, saveDecisionInsightsRating } from "@Helpers/decision-insights-rating-storage";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { getSessionResultsScenarioAnalytics } from "@hooks/game/activity/session/session-results-analytics";
import { getSessionFeedbackReplayResultId } from "@hooks/game/activity/session/session-results.utils";
import { SessionFeedbackModel, useSessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import useI18n from "@i18n";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";

interface IFeedbackSection {
  showHeader?: boolean;
}

const FeedbackSection = ({ showHeader = true }: IFeedbackSection) => {
  const { track } = useAnalytics();
  const { i18n } = useI18n();
  const { currentTab } = useSessionResultsTabContext();
  const { sessionResults, userId } = useSessionResultsStaticContext();
  const [isBetaDialogOpen, setIsBetaDialogOpen] = useState(false);
  const [userRating, setUserRating] = useState<UserRating>(null);
  const cognitiveResultId = getSessionFeedbackReplayResultId(sessionResults, currentTab);

  useEffect(() => {
    setUserRating(loadDecisionInsightsRating({ cognitiveResultId, userId }));
  }, [cognitiveResultId, userId]);

  const handleRate = (rating: DecisionInsightsRating) => {
    const newRating = userRating === rating ? null : rating;
    setUserRating(newRating);
    saveDecisionInsightsRating({ cognitiveResultId, userId }, newRating);
    const scenarioAnalytics = getSessionResultsScenarioAnalytics(sessionResults, currentTab, i18n);
    track(AnalyticsEvents.decision_engine_rated, {
      rating: newRating,
      scenario_id: scenarioAnalytics?.scenario_id ?? null,
      cognitiveResultId,
    });
  };

  const feedbackModel: SessionFeedbackModel = useSessionFeedbackModel({
    currentTab,
    cognitiveResultId,
    userId: userId || "",
    sessionResults,
  });

  if (currentTab <= 0) {
    return null;
  }

  return (
    <Stack spacing={2} paddingX={4}>
      {(showHeader || sessionResults?.game_mode === GameModes.SCENARIO) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          {showHeader && (
            <Stack flex={1} minWidth={0}>
              <FeedbackHeader onLearnMoreClick={() => setIsBetaDialogOpen(true)} onRate={handleRate} userRating={userRating} />
            </Stack>
          )}
          <Stack marginLeft="auto">
            <ScenarioRetry originalDecisions={feedbackModel.comparison.originalDecisions} />
          </Stack>
        </Stack>
      )}

      <DecisionScoresSection feedbackModel={feedbackModel} />

      <VideoStream feedbackModel={feedbackModel} />

      {showHeader && <BetaLearnMoreDialog open={isBetaDialogOpen} onClose={() => setIsBetaDialogOpen(false)} />}
    </Stack>
  );
};

export default FeedbackSection;
